import React, { useCallback, useState } from 'react'
import DataGridV2 from '../../../components/DataGridV2';
import { apps, LOCAL_STORAGE_GRID_KEYS } from '../../../_constants';
import { useTranslation } from 'react-i18next';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';
import { INITIAL_ROW_SELECTION_MODEL } from '../../../components/DataGridV2/constants';
import { formatDate, getLocalStorageItem, setLocalStorageItem } from '../../../_helpers';
import { getExistingRepairRequestImageColumns } from './utils';
import { makeStyles } from '@mui/styles';
import { Box, Container, Grid, Popover, Typography } from '@mui/material';
import { URLS } from '../../../apis/urls';
import ImagesContainer from '../../Survey/components/ImagesContainer';

const ExistingRepairRequestImages = ({ data, bottomDrawerClose, closeUploadDialog, onselect, prefix, title }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const apiRef = useGridApiRef();
  const app = useSelector(state => state.app);
  const { dossier: { imagesFromRepairRequest }, buildings: { selected } } = useSelector(state => state);
  const keyPrefix = app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [paginationModel, setPaginationModel] = useState(() =>
    getLocalStorageItem(LOCAL_STORAGE_GRID_KEYS[`${prefix}_PAGINATION`]?.[keyPrefix]) ||
    { page: 0, pageSize: 10 }
  );
  const [isOpenAttachments, setIsOpenAttachments] = useState(false);
  const [selectedRequestData, setSelectedRequestData] = useState([]);
  const [imageContainerOpen, setImageContainerOpen] = useState({ element: null, data: [] });

  const rows = data?.map((row, index) => ({
    id: row?.requestId || index,
    ...row,
  }));

  const handleOpenImageModal = (id, event) => {
    event.stopPropagation()
    setIsOpenAttachments(true);
    const allRequests = Object.keys(imagesFromRepairRequest).reduce((p, c) => [...p, ...imagesFromRepairRequest[c]], []);
    const selectedRequest = allRequests.find(p => p.requestId === id);
    if (selectedRequest?.attachments) {
      setSelectedRequestData(selectedRequest.attachments.map(p => ({ attachmentId: p.id, requestId: id, name: p.name })));
    } else {
      setSelectedRequestData([]);
    }
  };

  const handleImageContainerOpen = (e, data) => {
    const { currentTarget } = e;
    e.stopPropagation();
    setImageContainerOpen({ element: currentTarget, data });
  };
  
  const statusOptions = [
        ...new Set(data.map((item) => item.status))
    ];
    
  const columns = getExistingRepairRequestImageColumns({ t, keyPrefix, i18n, apiRef, selected, classes, handleImageContainerOpen, prefix ,statusOptions});

  const getStandardOptionsPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
    const userSelectedRows = isAllSelected ? data : idsToExport.map(id => data?.find(item => item?.requestId === id));
    const results = userSelectedRows?.map((item) => {
      let result = {};
      Object.keys(item).forEach((key) => {
        let value = item[key];
        const isDateField = columns.some(x => x.field === key && x.type === 'date');
        if (isDateField) {
          value = item[key] ? formatDate(new Date(item[key])) : "";
        }
        result[key] = value;
      });
      return result;
    });

    return results;
  };

  const onPaginationModelChange = useCallback((newPaginationModel) => {
    const fallback = { page: 0, pageSize: 10 };
    const normalizedPagination = {
      page: newPaginationModel?.page ?? fallback.page,
      pageSize: newPaginationModel?.pageSize ?? fallback.pageSize
    };
    setPaginationModel(normalizedPagination);
    setLocalStorageItem(LOCAL_STORAGE_GRID_KEYS[`${prefix}_PAGINATION`]?.[keyPrefix], normalizedPagination);
  }, [keyPrefix, prefix]);

  return (
    <Container maxWidth={false} className={classes.mainContainer}>
      <Box display="flex" flexDirection="column">
        <Box p={1} display="flex" alignItems="center">
          <Typography variant='h6'>
            {title}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGridV2
            localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS[`${prefix}_SEARCH_TEXT`]?.[keyPrefix]}
            localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS[`${prefix}_COLUMN_FILTER`]?.[keyPrefix]}
            localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS[`${prefix}_COLUMN_SORT`]?.[keyPrefix]}
            localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS[`${prefix}_COLUMN_ORDER`]?.[keyPrefix]}
            localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS[`${prefix}_COLUMN_WIDTH`]?.[keyPrefix]}
            localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS[`${prefix}_COLUMN_VISIBILITY`]?.[keyPrefix]}
            apiRef={apiRef}
            checkboxSelection
            rows={rows}
            columns={columns}
            pagination
            filterMode="client"
            {...(!!paginationModel && { paginationModel })}
            onPaginationModelChange={onPaginationModelChange}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            toolbarProps={{
              selectedRows: rowSelectionModel,
              getData: getStandardOptionsPrintAndDownload,
              title: title,
            }}
            onRowClick={(params, event) => {
              const rowId = params?.row?.requestId || params?.id;
              handleOpenImageModal(rowId, event);
            }}
          />
        </Box>
      </Box>

      <Popover
        classes={{ paper: classes.imageContainerPaper }}
        id="image-container"
        open={!!imageContainerOpen.element}
        anchorEl={imageContainerOpen.element}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setImageContainerOpen({ element: null, data: [] })}
      >
        <Grid className={classes.filesContainer} size={{ xs: 12 }}>
          <Grid container className={classes.gridContainer}>
            {imageContainerOpen.data && imageContainerOpen.data.map((p) => (
              <Grid key={p.attachmentId} className={classes.attachmentItemContainer}>
                <Grid>
                  <Grid
                    className={classes.thumnailContainers}
                    container
                    sx={{ justifyContent: "center" }}
                    size={{ xs: 12 }}
                  >
                    <div
                      className={classes.thumbnails}
                      style={{ backgroundImage: `url(${URLS.GET_ATTACHMENT_THUMBNAIL}${p.attachmentId})` }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Popover>

      {isOpenAttachments && (
        <ImagesContainer
          data={selectedRequestData}
          onSelectFiles={(data) => {
            setIsOpenAttachments(false);
            bottomDrawerClose();
            closeUploadDialog();
            onselect(data);
          }}
          open={isOpenAttachments}
          onClose={() => setIsOpenAttachments(false)}
          isImagesContainer
        />
      )}
    </Container>
  );
};


const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "visible",
    padding: 0,
  },
  thumnailContainers: {
    width: 61,
    height: 56,
    cursor: 'pointer',
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'space-evenly',

  },
  imageContainerPaper: {
    maxWidth: 382,
    width: '80%',
  },
  filesContainer: {
    overflow: 'auto',
    padding: theme.spacing(1.25),
    maxHeight: 360,
  },
  gridContainer: {
    justifyContent: 'space-around',
    gridTemplateColumns: 'repeat(auto-fill, 61px)',
    display: 'grid',
    gap: 10
  },
  attachmentItemContainer: {
    position: 'relative',
    background: theme.palette.common.white,
    boxShadow: '3px 3px 4px #eee',
    height: 56,
    overflow: 'hidden',
    borderRadius: 5,
    '&:hover': {
      "& $overlay": {
        opacity: 1
      },
    },
  },
  thumbnailImage: {
    marginLeft: theme.spacing(0),
    width: 50,
    height: 50,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundColor: 'rgba(0,0,0,0.1)',
    cursor: 'pointer'
  },
  thumbnails: {
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    height: '100%',
    width: '100%',
    display: 'block',

  },
}));
export default ExistingRepairRequestImages  
